// UploadTemplateModal.js
import React, { useState, useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import PreviewCanvas from '../PreviewCanvas';
import './UploadTemplateModal.css';
import '../TemplatesModal/TemplatesModal.css';

const subjectOptions = [
  'Literacy',
  'Maths',
  'Science',
  'Art',
  'Communication',
];

/*
 * Frontend-only subject labels.
 *
 * Buttons still use the original subject values when selecting, editing
 * and saving templates, so existing stored data remains compatible.
 */
const subjectDisplayNames = {
  Literacy: 'Literacy & Phonics',
  Maths: 'Maths',
  Science: 'Science & Investigation',
  Art: 'Art & Crafts',
  Communication: 'Communication & Language',
};

const levelOptions = ['1', '2', '3'];

const POPULAR_TAGS_URL =
  'https://edugroove.com/edugroove_backend/templates/PopularTags.json';

/* Used only if the public JSON file cannot be loaded. */
const fallbackPopularTagsConfig = {
  specialOccasions: [
    { label: 'Easter', uploadTag: 'Easter' },
    { label: 'Halloween', uploadTag: 'Halloween' },
    { label: 'Christmas', uploadTag: 'Christmas' },
  ],
  popularTags: [
    { label: 'Continuous Provision', uploadTag: 'Continuous Provision' },
    { label: 'Games', uploadTag: 'Game' },
    { label: 'Fine Motor Activities', uploadTag: 'Fine Motor' },
    { label: 'Flashcards', uploadTag: 'Flashcards' },
    { label: 'Cutting Activities', uploadTag: 'Cut' },
    { label: 'Twinkl Phonics', uploadTag: 'Twinkl' },
    { label: 'Jolly Phonics', uploadTag: 'Jolly Phonics' },
  ],
};

const YOUTUBE_URL_PATTERN =
  /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//i;

const normaliseList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normaliseQuickTagOption = (option) => {
  if (!option || typeof option !== 'object') return null;

  const label = String(
    option.label || option.uploadTag || option.value || ''
  ).trim();
  const uploadTag = String(
    option.uploadTag || option.value || label
  ).trim();

  return label && uploadTag ? { label, uploadTag } : null;
};

const UploadTemplateModal = ({
  onClose,
  onUpload,
  pages,
  categories,
  user,
  editingTemplate,
  existingTemplates = [],
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState(categories[0] || '');
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [tags, setTags] = useState('');
  const [color, setColor] = useState('#f0f0f0');
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [overwriteCurrent, setOverwriteCurrent] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [popularTagsConfig, setPopularTagsConfig] = useState(
    fallbackPopularTagsConfig
  );

  useEffect(() => {
    let isCancelled = false;

    fetch(`${POPULAR_TAGS_URL}?v=${Date.now()}`, {
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Popular tags request failed: ${response.status}`);
        }

        return response.json();
      })
      .then((config) => {
        if (
          !isCancelled &&
          config &&
          (Array.isArray(config.popularTags) ||
            Array.isArray(config.specialOccasions))
        ) {
          setPopularTagsConfig(config);
        }
      })
      .catch((error) => {
        console.warn('Using fallback popular tags:', error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (editingTemplate) {
      setName(editingTemplate.name || '');

      setCategory(
        editingTemplate.category ||
        categories[0] ||
        ''
      );

      /*
       * Existing templates normally use:
       * subject: "Literacy, Communication"
       *
       * Some newer data may use:
       * subjects: ["Literacy", "Communication"]
       *
       * This supports both formats.
       */
      const existingSubjects =
        normaliseList(editingTemplate.subjects).length > 0
          ? normaliseList(editingTemplate.subjects)
          : normaliseList(editingTemplate.subject);

      setSubjects(existingSubjects);

      /*
       * Existing templates normally use:
       * level: "1, 2"
       *
       * Some newer data may use:
       * levels: ["1", "2"]
       */
      const existingLevels =
        normaliseList(editingTemplate.levels).length > 0
          ? normaliseList(editingTemplate.levels)
          : normaliseList(editingTemplate.level);

      setLevels(existingLevels);

      setTags(
        Array.isArray(editingTemplate.tags)
          ? editingTemplate.tags.join(', ')
          : editingTemplate.tags || ''
      );

      setColor(editingTemplate.color || '#f0f0f0');
      setVideoUrl(editingTemplate.videoUrl || '');
      setVideoAspectRatio(
        editingTemplate.videoAspectRatio === '9:16' ? '9:16' : '16:9'
      );
      return;
    }

    /*
     * Reset the form when creating a completely new template.
     */
    setName('');
    setCategory(categories[0] || '');
    setSubjects([]);
    setLevels([]);
    setTags('');
    setColor('#f0f0f0');
    setVideoUrl('');
    setVideoAspectRatio('16:9');
    setOverwriteCurrent(false);
  }, [editingTemplate, categories]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleSubmit = () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      alert('Please enter a template name.');
      return;
    }

    const trimmedVideoUrl = videoUrl.trim();

    if (trimmedVideoUrl && !YOUTUBE_URL_PATTERN.test(trimmedVideoUrl)) {
      alert('Please enter a valid YouTube link, or leave the video field empty.');
      return;
    }

    const baseName = trimmedName;
    const baseFileName = trimmedName.replace(/\s+/g, '_');

    let finalName = baseName;
    let finalFileName = `${baseFileName}.json`;
    let counter = 1;

    const existingNames = existingTemplates.map((template) =>
      String(template.name || '').trim().toLowerCase()
    );

    const existingFiles = existingTemplates.map((template) =>
      String(template.file || '').trim().toLowerCase()
    );

    if (!overwriteCurrent) {
      while (
        existingNames.includes(finalName.toLowerCase()) ||
        existingFiles.includes(finalFileName.toLowerCase())
      ) {
        finalName = `${baseName} (${counter})`;
        finalFileName = `${baseFileName}_${counter}.json`;
        counter += 1;
      }
    }

    const cleanSubjects = subjects
      .map((subject) => subject.trim())
      .filter(Boolean);

    const cleanLevels = levels
      .map((level) => level.trim())
      .filter(Boolean);

    const cleanTags = tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const data = {
      name: finalName,
      file: finalFileName,
      color,
      category,

      /*
       * Singular versions are used by TemplatesModal and your
       * existing template index format.
       */
      subject: cleanSubjects.join(', '),
      level: cleanLevels.join(', '),

      /*
       * Array versions are retained for compatibility with any
       * upload code that already expects them.
       */
      subjects: cleanSubjects,
      levels: cleanLevels,

      tags: cleanTags,
      videoUrl: trimmedVideoUrl,
      videoAspectRatio: trimmedVideoUrl ? videoAspectRatio : '',
      createdBy: user?.email || '',
      createdAt: new Date().toISOString().slice(0, 10),
      overwrite: overwriteCurrent,
      pages,
    };

    onUpload(data);
  };

  const handleAddCategory = () => {
    const trimmedCategory = newCategory.trim();

    if (!trimmedCategory) {
      return;
    }

    const categoryAlreadyExists = categories.some(
      (existingCategory) =>
        existingCategory.toLowerCase() === trimmedCategory.toLowerCase()
    );

    if (!categoryAlreadyExists) {
      categories.push(trimmedCategory);
    }

    setCategory(trimmedCategory);
    setShowNewCategoryInput(false);
    setNewCategory('');
  };

  const toggleSubject = (subject) => {
    setSubjects((previousSubjects) =>
      previousSubjects.includes(subject)
        ? previousSubjects.filter(
            (existingSubject) => existingSubject !== subject
          )
        : [...previousSubjects, subject]
    );
  };

  const toggleLevel = (level) => {
    setLevels((previousLevels) =>
      previousLevels.includes(level)
        ? previousLevels.filter(
            (existingLevel) => existingLevel !== level
          )
        : [...previousLevels, level]
    );
  };

  const specialOccasionTags = Array.isArray(
    popularTagsConfig.specialOccasions
  )
    ? popularTagsConfig.specialOccasions
        .map(normaliseQuickTagOption)
        .filter(Boolean)
    : [];

  const popularQuickTags = Array.isArray(popularTagsConfig.popularTags)
    ? popularTagsConfig.popularTags
        .map(normaliseQuickTagOption)
        .filter(Boolean)
    : [];

  const isQuickTagSelected = (uploadTag) =>
    normaliseList(tags).some(
      (tag) => tag.toLowerCase() === uploadTag.toLowerCase()
    );

  const toggleQuickTag = (uploadTag) => {
    const existingTags = normaliseList(tags);
    const alreadySelected = existingTags.some(
      (tag) => tag.toLowerCase() === uploadTag.toLowerCase()
    );

    const nextTags = alreadySelected
      ? existingTags.filter(
          (tag) => tag.toLowerCase() !== uploadTag.toLowerCase()
        )
      : [...existingTags, uploadTag];

    setTags(nextTags.join(', '));
  };

  const previewTitle = name.trim() || 'Untitled Template';

  return (
    <div
      className="upload-template-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-template-modal-title"
    >
      <FocusTrap>
        <div className="upload-template-modal">
          <div className="upload-template-header">
            <h2
              id="upload-template-modal-title"
              className="upload-template-title"
            >
              {editingTemplate ? 'Update Template' : 'Upload Template'}
            </h2>

            <button
              type="button"
              className="upload-template-close-btn"
              onClick={onClose}
              aria-label="Close Upload Template Modal"
            >
              ×
            </button>
          </div>

          <div className="upload-template-body">
            <aside className="upload-template-preview-section">
              <h3 className="upload-template-section-title">
                Template Preview
              </h3>

              <div className="upload-template-preview">
                <div
                  className="template-card upload-template-preview-card"
                  style={{ backgroundColor: color }}
                >
                  {pages?.[0] ? (
                    <div className="preview-container">
                      <div className="preview-padding-wrapper">
                        <PreviewCanvas
                          page={pages[0]}
                          maxWidth={180}
                          maxHeight={280}
                          className="fade-in"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="template-image placeholder">
                      No preview available
                    </div>
                  )}

                  <div className="template-name-with-badge">
                    <p className="template-name">
                      {previewTitle}
                    </p>
                  </div>
                </div>
              </div>

              <p className="upload-template-preview-help">
                The card updates as you change the title and colour.
              </p>
            </aside>

            <div className="upload-template-form">
              <div className="upload-modal-fields">
                <section className="upload-template-form-section">
                  <h3 className="upload-template-section-title">
                    Template Details
                  </h3>

                  <div className="upload-modal-form-row">
                    <label htmlFor="upload-template-name">
                      Template Name
                    </label>

                    <input
                      id="upload-template-name"
                      type="text"
                      placeholder="Enter a template name"
                      value={name}
                      onChange={(event) =>
                        setName(event.target.value)
                      }
                    />
                  </div>

                  <div className="upload-modal-form-row">
                    <label htmlFor="upload-template-category">
                      Category
                    </label>

                    <div className="upload-template-category-row">
                      <select
                        id="upload-template-category"
                        value={category}
                        onChange={(event) =>
                          setCategory(event.target.value)
                        }
                      >
                        {categories.map((categoryName) => (
                          <option
                            key={categoryName}
                            value={categoryName}
                          >
                            {categoryName.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="show-category-btn"
                        onClick={() =>
                          setShowNewCategoryInput(
                            (currentValue) => !currentValue
                          )
                        }
                      >
                        {showNewCategoryInput
                          ? 'Cancel'
                          : '+ New Category'}
                      </button>
                    </div>
                  </div>

                  {showNewCategoryInput && (
                    <div className="upload-modal-form-row upload-template-new-category">
                      <label htmlFor="upload-template-new-category">
                        New Category
                      </label>

                      <div className="upload-template-category-row">
                        <input
                          id="upload-template-new-category"
                          type="text"
                          placeholder="Enter a new category"
                          value={newCategory}
                          onChange={(event) =>
                            setNewCategory(event.target.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleAddCategory();
                            }
                          }}
                        />

                        <button
                          type="button"
                          className="add-category-btn"
                          onClick={handleAddCategory}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </section>

                <section className="upload-template-form-section">
                  <h3 className="upload-template-section-title">
                    Tutorial Video
                  </h3>

                  <div className="upload-modal-form-row">
                    <label htmlFor="upload-template-video-url">
                      YouTube Link
                    </label>

                    <input
                      id="upload-template-video-url"
                      type="url"
                      inputMode="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(event) => setVideoUrl(event.target.value)}
                    />

                    <span className="upload-template-field-help">
                      Optional. Add a video showing how to make or play this activity.
                    </span>
                  </div>

                  {videoUrl.trim() && (
                    <div className="upload-modal-form-row">
                      <label>Video Shape</label>

                      <div className="upload-modal-button-group">
                        {['16:9', '9:16'].map((ratio) => (
                          <button
                            key={ratio}
                            type="button"
                            className={`upload-modal-toggle-btn ${
                              videoAspectRatio === ratio ? 'active' : ''
                            }`}
                            onClick={() => setVideoAspectRatio(ratio)}
                            aria-pressed={videoAspectRatio === ratio}
                          >
                            {ratio === '16:9' ? 'Landscape 16:9' : 'Portrait 9:16'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                <section className="upload-template-form-section">
                  <h3 className="upload-template-section-title">
                    Classification
                  </h3>

                  <div className="upload-modal-form-row">
                    <label>Subjects</label>

                    <div className="upload-modal-button-group">
                      {subjectOptions.map((subject) => (
                        <button
                          key={subject}
                          type="button"
                          className={`upload-modal-toggle-btn ${
                            subjects.includes(subject)
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => toggleSubject(subject)}
                          aria-pressed={subjects.includes(subject)}
                        >
                          {subjectDisplayNames[subject] || subject}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="upload-modal-form-row">
                    <label>Level</label>

                    <div className="upload-modal-button-group">
                      {levelOptions.map((level) => (
                        <button
                          key={level}
                          type="button"
                          className={`upload-modal-toggle-btn ${
                            levels.includes(level)
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => toggleLevel(level)}
                          aria-pressed={levels.includes(level)}
                        >
                          {level === '1'
                            ? 'Level 1'
                            : level === '2'
                              ? 'Level 2'
                              : 'Level 3'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="upload-modal-form-row">
                    <label>Special Occasions</label>

                    <div className="upload-modal-button-group upload-quick-tags">
                      {specialOccasionTags.map((tag) => (
                        <button
                          key={`occasion-${tag.uploadTag}`}
                          type="button"
                          className={`upload-modal-toggle-btn upload-quick-tag-btn ${
                            isQuickTagSelected(tag.uploadTag)
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => toggleQuickTag(tag.uploadTag)}
                          aria-pressed={isQuickTagSelected(tag.uploadTag)}
                          title={`Add the ${tag.uploadTag} tag`}
                        >
                          {tag.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="upload-modal-form-row">
                    <label>Popular Tags</label>

                    <div className="upload-modal-button-group upload-quick-tags">
                      {popularQuickTags.map((tag) => (
                        <button
                          key={`popular-${tag.uploadTag}`}
                          type="button"
                          className={`upload-modal-toggle-btn upload-quick-tag-btn ${
                            isQuickTagSelected(tag.uploadTag)
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => toggleQuickTag(tag.uploadTag)}
                          aria-pressed={isQuickTagSelected(tag.uploadTag)}
                          title={`Add the ${tag.uploadTag} tag`}
                        >
                          {tag.label}
                        </button>
                      ))}
                    </div>

                    <span className="upload-template-field-help">
                      Click a button to add or remove its tag.
                    </span>
                  </div>

                  <div className="upload-modal-form-row">
                    <label htmlFor="upload-template-tags">
                      Additional Tags
                    </label>

                    <input
                      id="upload-template-tags"
                      type="text"
                      placeholder="phonics, counting, worksheet"
                      value={tags}
                      onChange={(event) =>
                        setTags(event.target.value)
                      }
                    />

                    <span className="upload-template-field-help">
                      Separate each tag with a comma.
                    </span>
                  </div>
                </section>

                <section className="upload-template-form-section">
                  <h3 className="upload-template-section-title">
                    Card Appearance
                  </h3>

                  <div className="upload-modal-form-row">
                    <label htmlFor="upload-template-colour">
                      Card Colour
                    </label>

                    <div className="upload-template-colour-row">
                      <input
                        id="upload-template-colour"
                        type="color"
                        value={color}
                        onChange={(event) =>
                          setColor(event.target.value)
                        }
                      />

                      <span className="upload-template-colour-value">
                        {color.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </section>

                <section className="upload-template-form-section upload-template-overwrite-section">
                  <label className="upload-modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={overwriteCurrent}
                      onChange={(event) =>
                        setOverwriteCurrent(event.target.checked)
                      }
                    />

                    <span>
                      <strong>Overwrite current template</strong>
                      <small>
                        Replace the existing template instead of
                        creating a numbered copy.
                      </small>
                    </span>
                  </label>
                </section>
              </div>
            </div>
          </div>

          <div className="upload-template-footer">
            <button
              type="button"
              className="upload-template-submit-btn"
              onClick={handleSubmit}
            >
              {editingTemplate
                ? 'Update Template'
                : 'Upload Template'}
            </button>
          </div>
        </div>
      </FocusTrap>
    </div>
  );
};

export default UploadTemplateModal;
